import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useConsent } from '../../contexts/ConsentContext';
import styles from './CookieConsent.module.css';
import { posthog } from '../../lib/analytics/posthogClient';

/**
 * Font usati dal banner (stessa famiglia e pesi di index.html): testo 400, link 500, titolo e
 * pulsanti 600.
 */
const BANNER_FONTS = [
  '400 14px "Plus Jakarta Sans"',
  '500 14px "Plus Jakarta Sans"',
  '600 14px "Plus Jakarta Sans"',
];

/** Oltre questo tempo il banner si mostra comunque, con i font di ripiego. */
const FONT_WAIT_MS = 2500;

/**
 * T106-FE: `true` quando i font del banner sono pronti (caricati, falliti, scaduto il tempo
 * massimo, oppure il browser non espone `document.fonts`). Il banner è ancorato al bordo inferiore:
 * se compare con il font di ripiego e il testo si ridispone quando arriva Plus Jakarta Sans
 * (2 righe -> 3 su desktop), il suo bordo superiore sale e il banner conta come salto di layout
 * (CLS 0,137 su home desktop in produzione). Mostrandolo a font pronti nasce con la geometria
 * definitiva. Senza una @font-face corrispondente (foglio bloccato) `load()` termina subito, quindi
 * il banner non è mai in ritardo più di `FONT_WAIT_MS`. Solo presentazione: il consenso non cambia.
 */
function useBannerFontsSettled(enabled: boolean): boolean {
  const [settled, setSettled] = useState(() => typeof document === 'undefined' || !document.fonts);

  useEffect(() => {
    if (!enabled || settled) return;
    let cancelled = false;
    const done = () => {
      if (!cancelled) setSettled(true);
    };
    const timer = window.setTimeout(done, FONT_WAIT_MS);
    Promise.all(BANNER_FONTS.map((font) => document.fonts.load(font))).then(done, done);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [enabled, settled]);

  return settled;
}

/**
 * GDPR-compliant cookie consent banner.
 *
 * Renders at the bottom of the viewport when consent has not yet been recorded.
 * Offers three actions: accept all, reject all, or customise granularly.
 *
 * T106-FE: l'elemento è un `section` (non un `aside`, il cui ruolo implicito
 * "complementary" non ammette `dialog`).
 *
 * T210-FE: il banner è reso con un portale direttamente in `document.body`, fuori da `#root`. Sulle
 * pagine con il guscio server `#root` resta `visibility:hidden` e `inert` fino al rilascio del guscio
 * (T102, `utils/ssrShell.ts`): dentro `#root` il banner, che è l'elemento LCP di evento e categoria,
 * non si dipingeva né era attivabile prima di quel momento. Fuori da `#root` si dipinge appena
 * montato. Gli elementi sono `position: fixed`, quindi fuori dal flusso (anche dalla griglia che il
 * guscio applica al body): nessuno spostamento. Il portale è sempre attivo, non solo a guscio
 * pendente: cambiare genitore al rilascio rimonterebbe il banner perdendo il pannello aperto.
 * I contesti React (router, consenso, PostHog) attraversano il portale; il consenso non cambia.
 */
export function CookieConsent() {
  const { showBanner, acceptAll, updatePreferences } = useConsent();
  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsToggle, setAnalyticsToggle] = useState(false);
  const fontsSettled = useBannerFontsSettled(showBanner);

  const handleCustomizeToggle = useCallback(() => {
    setShowCustomize((prev) => !prev);
  }, []);

  const handleAcceptAll = useCallback(() => {
    acceptAll();
    // Capture after opt-in is triggered by acceptAll()
    posthog.capture('consent_accepted', { method: 'accept_all' });
  }, [acceptAll]);

  const handleSave = useCallback(() => {
    updatePreferences({ analytics: analyticsToggle });
    if (analyticsToggle) {
      posthog.capture('consent_accepted', { method: 'custom' });
    }
  }, [analyticsToggle, updatePreferences]);

  if (!showBanner || !fontsSettled || typeof document === 'undefined') return null;

  return createPortal(
    <>
      {/* Dim backdrop */}
      <div className={styles.backdrop} aria-hidden="true" />

      <section
        className={styles.banner}
        role="dialog"
        aria-label="Consenso cookie"
        aria-modal="false"
      >
        <div className={styles.inner}>
          <h2 className={styles.heading}>Questo sito utilizza i cookie</h2>
          <p className={styles.body}>
            Utilizziamo cookie analitici per capire come utilizzi il sito e migliorare
            l&apos;esperienza. Nessun dato viene condiviso con terze parti per pubblicit&agrave;.
            Maggiori informazioni nella <Link to="/cookie-policy">Cookie Policy</Link>.
          </p>

          {/* Main action buttons */}
          <div className={styles.actions}>
            <button type="button" className={styles.btnPrimary} onClick={handleAcceptAll}>
              Accetta tutto
            </button>
            <button type="button" className={styles.btnLink} onClick={handleCustomizeToggle}>
              {showCustomize ? 'Chiudi' : 'Personalizza'}
            </button>
          </div>

          {/* Customise panel */}
          {showCustomize && (
            <div className={styles.customizePanel}>
              {/* Necessary cookies (always on) */}
              <div className={styles.toggleRow}>
                <div className={styles.toggleInfo}>
                  <span className={styles.toggleLabel}>Cookie necessari</span>
                  <span className={styles.toggleDesc}>
                    Sempre attivi per il funzionamento del sito
                  </span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked="true"
                  aria-label="Cookie necessari (sempre attivi)"
                  className={styles.switch}
                  disabled
                >
                  <span className={styles.switchThumb} />
                </button>
              </div>

              {/* Analytics cookies */}
              <div className={styles.toggleRow}>
                <div className={styles.toggleInfo}>
                  <span className={styles.toggleLabel}>Cookie analitici</span>
                  <span className={styles.toggleDesc}>
                    Ci aiutano a capire come navighi il sito
                  </span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={analyticsToggle ? 'true' : 'false'}
                  aria-label="Cookie analitici"
                  className={styles.switch}
                  onClick={() => setAnalyticsToggle((prev) => !prev)}
                >
                  <span className={styles.switchThumb} />
                </button>
              </div>

              <div className={styles.saveRow}>
                <button type="button" className={styles.btnSave} onClick={handleSave}>
                  Salva preferenze
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </>,
    document.body
  );
}
